'use client'

import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { useMutation, useQuery } from '@apollo/client'
import { SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import LanguageSwitcher from '@/components/shared/languageSelect'
import { useLanguage } from '@/context/LanguageContext'
import '@/components/styles/Form.css'
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'
import {
  CREATE_FABRIC_FEATURE_GROUP,
  DELETE_FABRIC_FEATURE_GROUP,
  GET_FABRIC_FEATURE_GROUP,
  UPDATE_FABRIC_FEATURE_GROUP,
} from '../fabricFeaturesQueries'
import { FabricFeatureGroupFormValues } from '../types'
import { SheetScrollableContent } from '@/components/ui/sheet-scrollable-content'
import { Icons } from '@/components/shared/icons'
import { useConfirmation } from '@/context/ConfirmationContext'

const formSchema = z.object({
  code: z.string().optional(),
  fabric_feature_group_profiles: z
    .array(
      z.object({
        id: z.number().optional(),
        name: z.string().optional(),
        language: z.string(),
      })
    )
    .transform((items, ctx) => {
      const filteredItems = items.filter(item => item.name?.trim() !== '')
      if (filteredItems.length === 0) {
        items.forEach((item, index) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'At least one language profile needs to be filled',
            path: [`[${index}].name`],
          })
        })
      }
      return filteredItems
    }),
}) satisfies z.ZodType<FabricFeatureGroupFormValues>

interface FabricFeatureGroupFormProps {
  data: {
    id: number | null
  }
  onDirtyChange?: (dirty: boolean) => void
  onRequestClose?: () => void
}

export default function FabricFeatureGroupForm({
  data,
  onDirtyChange,
  onRequestClose,
}: FabricFeatureGroupFormProps) {
  const { allLanguages, languageId } = useLanguage()
  const [id, setId] = useState(data?.id)
  const { showConfirmation } = useConfirmation()

  const {
    data: { findUniqueFabricFeatureGroup: groupData } = {},
    loading: loadingData,
  } = useQuery(GET_FABRIC_FEATURE_GROUP, {
    variables: { id },
    fetchPolicy: 'network-only',
    skip: !id,
  })

  const form = useForm<FabricFeatureGroupFormValues>({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      code: '',
      fabric_feature_group_profiles: [],
    },
  })

  const [updateGroup] = useMutation(UPDATE_FABRIC_FEATURE_GROUP, {
    onError: () => {
      toast.error('Failed to update feature group')
    },
    onCompleted: () => {
      toast.success('Feature group updated successfully')
    },
    refetchQueries: ['GET_FABRIC_FEATURES_TREE', 'GetFabricFeatureGroup'],
  })

  const [deleteGroup] = useMutation(DELETE_FABRIC_FEATURE_GROUP, {
    refetchQueries: ['GET_FABRIC_FEATURES_TREE'],
  })

  const handleDelete = () => {
    if (id) {
      const featureCount = groupData?.fabric_features?.length || 0
      showConfirmation({
        onConfirm: async () => {
          try {
            await deleteGroup({
              variables: { where: { id } },
            })
            toast.success('Feature group deleted successfully')
            onRequestClose?.()
          } catch (error) {
            toast.error('Failed to delete feature group')
          }
        },
        description: featureCount
          ? `This group contains ${featureCount} feature(s). Deleting this group will ungroup its features (they won't be deleted).`
          : 'Are you sure you want to delete this feature group? This action cannot be undone.',
        confirmText: 'Delete',
        cancelText: 'Cancel',
      })
    }
  }

  const [createGroup] = useMutation(CREATE_FABRIC_FEATURE_GROUP, {
    onError: () => {
      toast.error('Failed to create feature group')
    },
    onCompleted: response => {
      toast.success('Feature group created successfully')
      const newId = response.createFabricFeatureGroup.id
      setId(newId)
    },
    refetchQueries: ['GET_FABRIC_FEATURES_TREE'],
  })

  function onSubmit(values: FabricFeatureGroupFormValues) {
    if (id) {
      updateGroup({
        variables: {
          where: { id },
          data: {
            code: values.code,
            profiles: values.fabric_feature_group_profiles.map(p => ({
              id: p.id,
              language: p.language,
              name: p.name || '',
            })),
          },
        },
      })
    } else {
      createGroup({
        variables: {
          data: {
            code: values.code,
            profiles: values.fabric_feature_group_profiles.map(p => ({
              language: p.language,
              name: p.name || '',
            })),
          },
        },
      })
    }
  }

  useEffect(() => {
    if (!loadingData) {
      if (groupData) {
        const existingProfiles =
          groupData.fabric_feature_group_profiles?.filter(
            (p: any) => p.language && allLanguages.includes(p.language)
          ) ?? []

        const allProfiles = allLanguages.map(lang => {
          const existingProfile = existingProfiles.find(
            (p: any) => p.language === lang
          )
          if (existingProfile) {
            return {
              id: existingProfile.id,
              name: existingProfile.name ?? '',
              language: existingProfile.language,
            }
          } else {
            return {
              name: '',
              language: lang,
            }
          }
        })

        form.reset({
          code: groupData.code ?? '',
          fabric_feature_group_profiles: allProfiles,
        })
      } else {
        form.reset({
          code: '',
          fabric_feature_group_profiles: allLanguages.map(lang => ({
            name: '',
            language: lang,
          })),
        })
      }
    }
  }, [groupData, loadingData, allLanguages, form.reset])

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(form.formState.isDirty)
    }
  }, [form.formState.isDirty, onDirtyChange])

  return (
    <SheetContent className="sheet-content flex flex-col">
      <SheetHeader className="flex-shrink-0">
        <div className="flex justify-between items-center">
          <SheetTitle>Feature Group</SheetTitle>
          <LanguageSwitcher />
        </div>
      </SheetHeader>
      <SheetScrollableContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 max-w-3xl mx-auto py-5"
          >
            <div className="grid gap-4 py-4">
              <InputFieldContainer
                label="Group Name"
                description="This is the public display name."
                control={form.control}
                name={`fabric_feature_group_profiles.${languageId}.name`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="" type="text" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Code"
                description="Unique identifier code for this group."
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input
                        placeholder="e.g. material-type"
                        type="text"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex gap-2">
                {id && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleDelete}
                    className="shrink-0"
                  >
                    <Icons.trash className="h-4 w-4 text-red-500" />
                  </Button>
                )}
                <Button
                  type="submit"
                  disabled={loadingData}
                  className="flex-1"
                >
                  {id ? 'Update' : 'Create'}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </SheetScrollableContent>
    </SheetContent>
  )
}
